/**
 * Tones for billing state tags, following the same shape as `subscription-tag.js`.
 *
 * Semantic colour only — good / warning / critical / neutral. Deliberately not the brand accent:
 * these tags exist so an operator can scan 29 rows and see what needs attention without reading, and
 * mixing them with decorative colour would defeat that.
 */
const NEUTRAL = Object.freeze({ background: '#eef1f4', color: '#4e555d', border: '0px !important' });
const OK = Object.freeze({ background: '#e3efe7', color: '#2c6544', border: '0px !important' });
const WARNING = Object.freeze({ background: '#f6ebdc', color: '#9c5a0b', border: '0px !important' });
const CRITICAL = Object.freeze({ background: '#f5e4e4', color: '#9b2a2a', border: '0px !important' });
const INFO = Object.freeze({ background: '#e4f0ef', color: '#0a4f4f', border: '0px !important' });

/** Stripe subscription statuses. `unpaid` and `past_due` both mean money is owed. */
const SUBSCRIPTION_STATUS_MAP = Object.freeze({
  active: OK,
  trialing: INFO,
  past_due: CRITICAL,
  unpaid: CRITICAL,
  incomplete: WARNING,
  incomplete_expired: NEUTRAL,
  paused: WARNING,
  canceled: NEUTRAL,
  deleted: NEUTRAL,
});

/**
 * The four BI states from the plan.
 *
 * `paying-no-access` is critical on purpose: a client charged for a module they cannot open is a
 * refund and a support escalation, not a data-quality note. `unbilled` is a warning rather than
 * critical — it is revenue not collected, which is a work list, not an emergency.
 */
const BI_STATE_MAP = Object.freeze({
  'in-sync': OK,
  unbilled: WARNING,
  'paying-no-access': CRITICAL,
  'not-a-bi-client': NEUTRAL,
});

/** `expired`: trial over, nothing paid. `converted`: trial over, client stayed. */
const TRIAL_STATE_MAP = Object.freeze({
  none: NEUTRAL,
  active: INFO,
  expired: WARNING,
  converted: OK,
});

const CARD_TONE_MAP = Object.freeze({
  ok: OK,
  warning: WARNING,
  critical: CRITICAL,
  expired: CRITICAL,
});

export function getSubscriptionStatusStyle(status) {
  if (typeof status !== 'string') return NEUTRAL;
  return SUBSCRIPTION_STATUS_MAP[status.toLowerCase()] || NEUTRAL;
}

export function getBiStateStyle(biState) {
  if (typeof biState !== 'string') return NEUTRAL;
  return BI_STATE_MAP[biState] || NEUTRAL;
}

export function getTrialStateStyle(trialState) {
  if (typeof trialState !== 'string') return NEUTRAL;
  return TRIAL_STATE_MAP[trialState] || NEUTRAL;
}

export function getCardExpiryStyle(tone) {
  if (typeof tone !== 'string') return NEUTRAL;
  return CARD_TONE_MAP[tone] || NEUTRAL;
}
